// Fast, styled tooltips for the whole app. Any element with a `title` (set anywhere in the app) or a
// `data-tip` shows this tooltip ~150 ms after hover/focus instead of the browser's slow native one.
// The title is moved to data-tip on first hover so the native tooltip never appears; accessible names
// stay on aria-label / visible text. Rendered as a manual popover so it sits above modal dialogs.
import { pageZoom } from './action-menu.js';

const DELAY_MS = 150;

export function installTooltips(doc: Document = document): void {
  const tip = doc.createElement('div');
  tip.className = 'tooltip'; tip.setAttribute('role', 'tooltip'); tip.popover = 'manual';
  doc.body.append(tip);
  let timer: number | undefined;
  let target: HTMLElement | null = null;

  const hide = (): void => {
    window.clearTimeout(timer); target = null;
    if (tip.matches(':popover-open')) tip.hidePopover();
  };
  const show = (el: HTMLElement): void => {
    const text = el.dataset.tip; if (!text || !el.isConnected) return;
    tip.textContent = text;
    const zoom = pageZoom();
    const viewport = window.visualViewport;
    const width = viewport?.width ?? innerWidth; const height = viewport?.height ?? innerHeight;
    const x = viewport?.offsetLeft ?? 0; const y = viewport?.offsetTop ?? 0;
    tip.style.maxWidth = `${Math.max(1, Math.min(280, (width - 16) / zoom))}px`;
    tip.style.maxHeight = `${Math.max(1, (height - 16) / zoom)}px`;
    if (!tip.matches(':popover-open')) tip.showPopover();
    const box = el.getBoundingClientRect(); const own = tip.getBoundingClientRect();
    const above = box.top - own.height - 8;
    const preferred = above >= y + 8 ? above : box.bottom + 8;
    const top = Math.max(y + 8, Math.min(preferred, y + height - own.height - 8));
    const left = Math.max(x + 8, Math.min(box.left + box.width / 2 - own.width / 2, x + width - own.width - 8));
    tip.style.top = `${top / zoom}px`; tip.style.left = `${left / zoom}px`;
  };
  const arm = (el: HTMLElement): void => {
    if (el.title) { el.dataset.tip = el.title; el.removeAttribute('title'); }
    if (!el.dataset.tip || target === el) return;
    hide(); target = el;
    timer = window.setTimeout(() => { if (target === el) show(el); }, DELAY_MS);
  };
  const tipped = (node: EventTarget | null): HTMLElement | null =>
    node instanceof Element ? node.closest<HTMLElement>('[title],[data-tip]') : null;

  doc.addEventListener('pointerover', (event) => { const el = tipped(event.target); if (el) arm(el); else hide(); });
  doc.addEventListener('pointerout', (event) => { if (target && !target.contains(event.relatedTarget as Node | null)) hide(); });
  doc.addEventListener('focusin', (event) => { const el = tipped(event.target); if (el) arm(el); });
  doc.addEventListener('focusout', hide);
  doc.addEventListener('pointerdown', hide, true);
  doc.addEventListener('scroll', hide, true);
  window.addEventListener('blur', hide);
  window.addEventListener('resize', hide);
  window.visualViewport?.addEventListener('resize', hide);
  window.visualViewport?.addEventListener('scroll', hide);
}
